import { type ReactNode, createContext, useContext, useEffect, useState, useMemo } from 'react'
import type { TEnumItem, TEnumContextType } from '@modules/types'
import { convertApiResponse } from '@modules/api-core'

let enumCache: TEnumItem[] | null = null

export const fetchEnums = async (apiBaseUrl: string): Promise<TEnumItem[]> => {
  if (enumCache) return enumCache
  const API_ENUM_URL = `${apiBaseUrl}/enum/index`
  const response = await fetch(API_ENUM_URL, { credentials: 'include' })
  if (!response.ok) throw new Error('Failed to fetch enums')
  const data = await response.json()
  enumCache = data
  return convertApiResponse(data)
}

export const clearEnumCache = (): void => {
  enumCache = null
}

const EnumContext = createContext<TEnumContextType | undefined>(undefined)

export const useEnumContext = (): TEnumContextType => {
  const context = useContext(EnumContext)
  if (!context) {
    throw new Error('useEnumContext must be used within EnumProvider')
  }
  return context
}

interface EnumProviderProps {
  children: ReactNode
  apiBaseUrl: string
  isAuthenticated: boolean
  isAuthLoading: boolean
}

export const EnumProvider = ({
  children,
  apiBaseUrl,
  isAuthenticated,
  isAuthLoading,
}: EnumProviderProps) => {
  const [enums, setEnums] = useState<TEnumItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isAuthLoading) {
      return
    }

    if (!isAuthenticated) {
      clearEnumCache()
      setEnums([])
      setError(null)
      setLoading(false)
      return
    }

    fetchEnums(apiBaseUrl)
      .then(setEnums)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [isAuthenticated, isAuthLoading, apiBaseUrl])

  const contextValue = useMemo(() => ({ enums, loading, error }), [enums, loading, error])

  return <EnumContext.Provider value={contextValue}>{children}</EnumContext.Provider>
}
